import { useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import styled, { CSSObject } from '@emotion/styled'
import SideModal from '../Modal/SideModal'
import {
  useModal,
  useShop,
  useConsumerCart,
  useCheckout,
  useShopPage,
  useCartMatch,
  useSessionStorage
} from 'shop/hooks'
import { useQuantityDeltas } from 'shop/hooks/useQuantityDeltas'
import ThreeDotsLoader from 'shop/components/Loader/ThreeDotsLoader'
import { TrackableEvent } from 'tracker'
import { trackUserActionsFBPixel, trackUserActionsGA4 } from 'tracker'
import { useNavigate } from 'react-router-dom'
import {
  createEcommEventDataFromConsumerCart,
  merchantGA4EcommTrackViewCart,
  slerpGA4EcommTrackViewCart
} from 'tracker/GA/ecommerce'
import { OrderItem } from 'shop/types/cart'
import { StyledHTMLElement } from 'shop/theme/types'
import { Medium } from '../common/fontSizeTags'
import ShopCartHeader from './ShopCartHeader'
import EmptyCartState from './EmptyCartState'
import ShopCartItemList from './ShopCartItemList'
import { LineBreak } from '../Shop/commonStyles'
import { AlcoholNote } from '../Cart/AlcoholNote'
import ShopCartFooter from './ShopCartFooter'
import { formatMoney } from 'shop/utils/common'
import { thinScrollbar } from '../common'
import { DEFAULT_NAVBAR_HEIGHT } from '../NavBar/commonStyles'

interface Props {
  closeCartModal?: () => void
  isModal?: boolean
}

const ShopCart = ({ closeCartModal, isModal }: Props) => {
  const { closeModal, openModal, isModalOpen } = useModal()
  const navigate = useNavigate()
  const { partner } = useShop()
  const { cart, validateCart, cartLoading } = useConsumerCart()
  const {
    minimumOrderValueWarning,
    dynamicPrepTimeSlotValidation,
    hasAlcoholicProducts,
    handleOrderItemQuantityChange
  } = useCheckout()
  const { setProductErrorOnContinue } = useShopPage()
  const { isCartMatch } = useCartMatch()
  const { getSessionStorageItem: hasViewedUpsell } =
    useSessionStorage('upsell-modal')
  const [navBottomPosition, setNavBottomPosition] = useState('0px')
  const {
    pendingDeltas,
    updatePendingDelta,
    clearPendingDelta,
    getPendingDelta,
    resetPendingDelta
  } = useQuantityDeltas()

  const isMounted = useRef(true)

  const orderItems = cart?.orderItems
  const hasOrderItems = !!orderItems?.length

  const showFooter = isModal ? hasOrderItems : true
  const showEmptyState = !hasOrderItems || !isCartMatch

  /** Runs once on mount, sets isMounted & cleans up on unmount */
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  /** Calls validateCart (if there is a cart) on init to get initial errors/warnings */
  useEffect(() => {
    if (cart?.id) {
      validateCart(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id])

  // Ensure cart modal is below navbar on mobile
  useEffect(() => {
    if (!isModal) return

    const updateNavPosition = () => {
      const navBar = document.getElementById('nav-bar-component')
      if (navBar) {
        const rect = navBar.getBoundingClientRect()
        const bottomPosition = rect.bottom.toString()
        setNavBottomPosition(bottomPosition + 'px')
      }
    }

    // Initial position
    updateNavPosition()

    // Update on resize
    const resizeObserver = new ResizeObserver(updateNavPosition)
    const navBar = document.getElementById('nav-bar-component')
    if (navBar) {
      resizeObserver.observe(navBar)
    }

    // Cleanup resize observer
    return () => {
      resizeObserver.disconnect()
    }
  }, [isModal])

  /** Handles tracking on init */
  useEffect(() => {
    if (partner?.name && cart?.id) {
      if (!isMounted.current) return

      const { id, orderItems, store } = cart
      const { name: storeName } = store

      const eventData = {
        cart_id: id,
        products: orderItems.map((item) => ({
          product_id: item.variantId,
          name: item.name,
          currency: 'GBP',
          quantity: item.quantity,
          price: item.total.discounted || item.total.base
        }))
      }

      const ecommerceEventData = createEcommEventDataFromConsumerCart(
        cart,
        partner?.name,
        storeName
      )

      const body = {
        category: 'Cart',
        action: TrackableEvent.CartViewed
      }

      trackUserActionsGA4(body, 'slerpGA4Tracking')

      // legacy tracking
      trackUserActionsFBPixel(TrackableEvent.CartViewed, eventData)
      trackUserActionsGA4(body, 'merchantGA4Tracking')

      slerpGA4EcommTrackViewCart(ecommerceEventData)
      merchantGA4EcommTrackViewCart(ecommerceEventData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.id, partner?.name])

  const checkout = () => {
    if (!isModalOpen('upsell') && !hasViewedUpsell()) {
      openModal('upsell')
      return
    }
    if (!cart?.id) return
    closeModal('cart')
    return navigate('/checkout')
  }

  const hasCheckoutWarnings =
    !!minimumOrderValueWarning || !!dynamicPrepTimeSlotValidation

  const debouncedQuantityChange = useMemo(
    () =>
      debounce(
        async (orderItem: OrderItem, proceedWithNewOrderItems?: boolean) => {
          const accumulatedDelta = getPendingDelta(orderItem.id)

          const res = await handleOrderItemQuantityChange(
            orderItem,
            accumulatedDelta,
            proceedWithNewOrderItems
          )
          const { errors } = res || {}

          if (!errors?.length) {
            clearPendingDelta(orderItem.id)
            return
          }

          // Reset pending delta to 0 when there are errors, so UI shows original quantity
          resetPendingDelta(orderItem.id, 0)

          setProductErrorOnContinue(
            () => (proceedWithNewOrderItems?: boolean) =>
              handleOrderItemQuantityChange(
                orderItem,
                accumulatedDelta,
                proceedWithNewOrderItems
              )
          )
        },
        300
      ),
    [
      getPendingDelta,
      clearPendingDelta,
      resetPendingDelta,
      handleOrderItemQuantityChange,
      setProductErrorOnContinue
    ]
  )
  /** Accumulate the delta for the specific order item, trigger debounce function */
  const handleQuantityChange = async (
    orderItem: OrderItem,
    delta: number,
    proceedWithNewOrderItems?: boolean
  ): Promise<void> => {
    updatePendingDelta(orderItem.id, delta, orderItem.quantity)

    // Trigger the debounced function
    return debouncedQuantityChange(orderItem, proceedWithNewOrderItems)
  }

  // Cleanup debounced function on teardown
  useEffect(() => {
    return () => {
      debouncedQuantityChange.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ctaCopy = () => {
    if (showEmptyState) {
      return 'Your cart is empty'
    }
    if (minimumOrderValueWarning) {
      const { base: subtotal } = cart?.summary.subtotal || {}
      const minimumValue = minimumOrderValueWarning.minimumValue
      const amountToCheckout =
        parseFloat(minimumValue) - parseFloat(subtotal || '0')
      return `Spend ${formatMoney(amountToCheckout)} more to checkout`
    }
    if (dynamicPrepTimeSlotValidation?.restriction?.maxPossibleBasketValue) {
      const maxPossibleBasketValue =
        dynamicPrepTimeSlotValidation.restriction.maxPossibleBasketValue
      return (
        <DynamicPrepTimeCtaSpan>
          Order too large to prepare before closing.{' '}
          <Medium>
            Reduce to under {formatMoney(maxPossibleBasketValue)} to checkout.
          </Medium>
        </DynamicPrepTimeCtaSpan>
      )
    }
    return `Go to checkout`
  }

  const mainContent = (
    <>
      {showEmptyState && (
        <EmptyCartState closeModal={closeModal} isModal={!!isModal} />
      )}

      {isCartMatch && (
        <>
          <AlcoholSection hasAlcoholicProducts={hasAlcoholicProducts}>
            <IdRequiredContainer>
              <AlcoholNote />
            </IdRequiredContainer>
            <LineBreakContainer>
              <LineBreak height={'2px'} />
            </LineBreakContainer>
          </AlcoholSection>

          {hasOrderItems && (
            <CartItemListContainer>
              <ShopCartItemList
                orderItems={orderItems}
                onQuantityChange={handleQuantityChange}
                pendingDeltas={pendingDeltas}
              />
            </CartItemListContainer>
          )}
        </>
      )}

      {showFooter && (
        <ShopCartFooter
          cart={cart}
          cartLoading={cartLoading}
          hasCheckoutWarnings={hasCheckoutWarnings}
          checkout={checkout}
          ctaCopy={ctaCopy()}
          isCartMatch={isCartMatch}
        />
      )}
    </>
  )

  const cartContent = (
    <>
      <ShopCartHeader isModal={!!isModal} />
      {isModal ? mainContent : <ContentSection>{mainContent}</ContentSection>}
      {!isModal && cartLoading && (
        <LoadingContainer>
          <ThreeDotsLoader />
        </LoadingContainer>
      )}
    </>
  )

  return isModal && closeCartModal ? (
    <SideModal handleCloseModal={closeCartModal} top={navBottomPosition}>
      {cartContent}
    </SideModal>
  ) : (
    <StickyCartContainer>{cartContent}</StickyCartContainer>
  )
}

const StickyCartContainer = styled.div(({ theme }): CSSObject => {
  // navbar height(64px) + fulfillment height (78px) + 16px top margin  + 16px bottom margin
  const totalOffset = DEFAULT_NAVBAR_HEIGHT + 78 + 16 + 16
  return {
    backgroundColor: 'white',
    width: '100%',
    // Full viewport height minus total offset
    height: `calc(100vh - ${totalOffset}px)`,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    overflowY: 'auto',
    position: 'relative',
    // Ensures no scroll-chaining
    overscrollBehavior: 'contain',
    boxShadow: '0px 5px 32px rgba(0, 0, 0, 0.07)',
    ...thinScrollbar(theme)
  }
})

const DynamicPrepTimeCtaSpan = styled.span<StyledHTMLElement>({
  whiteSpace: 'normal',
  textAlign: 'center'
})

const ContentSection = styled.div({
  padding: '0 16px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
})

const CartItemListContainer = styled.div<StyledHTMLElement>({
  marginTop: '16px'
})

const LoadingContainer = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2
})

const AlcoholSection = styled.div<
  StyledHTMLElement & { hasAlcoholicProducts: boolean }
>(({ hasAlcoholicProducts }) => ({
  transition: 'all 0.3s ease',
  opacity: 0,
  maxHeight: 0,
  overflow: 'hidden',
  ...(hasAlcoholicProducts && {
    opacity: 1,
    maxHeight: '150px'
  })
}))

const IdRequiredContainer = styled.div({
  marginBottom: '16px'
})

const LineBreakContainer = styled.div<StyledHTMLElement & { margin?: string }>(
  ({ margin = '0 -16px 0px' }) => ({
    margin: margin
  })
)

export default ShopCart
